"use client";

import { useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import Link from "next/link";

type Politician = {
  id: string;
  name: string;
  position: string;
  jurisdiction: string;
  party: string;
  image_url: string | null;
  total_lobby_ars: number;
  total_lobby_usd: number;
  status: string;
};

const supabase = () =>
  createClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL || "",
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ""
  );

export default function Home() {
  const [politicians, setPoliticians] = useState<Politician[]>([]);
  const [search, setSearch] = useState("");
  const [totalArs, setTotalArs] = useState(0);
  const [totalUsd, setTotalUsd] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    if (!url || !key) return; // no crash if env vars missing
    void (async () => {
      try {
        const { data, error } = await supabase()
          .from("politicians")
          .select("*")
          .order("total_lobby_ars", { ascending: false });
        if (error) {
          console.error("Supabase error:", error.message);
          return;
        }
        if (data) {
          setPoliticians(data);
          setCount(data.length);
          setTotalArs(data.reduce((s, p) => s + (p.total_lobby_ars || 0), 0));
          setTotalUsd(data.reduce((s, p) => s + (p.total_lobby_usd || 0), 0));
        }
      } catch (err) {
        console.error("Supabase connection failed:", err);
      }
    })();
  }, []);

  const filtered = politicians.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.party.toLowerCase().includes(search.toLowerCase()) ||
      p.jurisdiction.toLowerCase().includes(search.toLowerCase())
  );

  const formatArs = (n: number) =>
    n >= 1e9
      ? `ARS $${(n / 1e9).toFixed(1)}B`
      : n >= 1e6
      ? `ARS $${(n / 1e6).toFixed(1)}M`
      : `ARS $${n.toLocaleString()}`;

  const formatUsd = (n: number) =>
    n >= 1e6
      ? `USD $${(n / 1e6).toFixed(1)}M`
      : `USD $${n.toLocaleString()}`;

  return (
    <main className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-b from-red-950 via-red-900 to-black px-4 py-16 text-center">
        <h1 className="text-5xl md:text-7xl font-black tracking-tighter text-white mb-4">
          LOBBY TRACKER
        </h1>
        <p className="text-2xl md:text-3xl text-red-400 font-bold">ARGENTINA 🇦🇷</p>
        <p className="mt-6 text-lg text-gray-400 max-w-2xl mx-auto">
          Rastreando la influencia del lobby israelí en la política argentina.
          Seguí el dinero. Seguí los contratos. Seguí las conexiones.
        </p>

        {/* Stats */}
        <div className="flex justify-center gap-8 mt-10 text-center">
          <div>
            <p className="text-4xl font-black text-white">{count}</p>
            <p className="text-sm text-gray-400">POLÍTICOS RASTREADOS</p>
          </div>
          <div>
            <p className="text-4xl font-black text-white">{formatArs(totalArs)}</p>
            <p className="text-sm text-gray-400">TOTAL ARS</p>
          </div>
          <div>
            <p className="text-4xl font-black text-white">{formatUsd(totalUsd)}</p>
            <p className="text-sm text-gray-400">TOTAL USD</p>
          </div>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Buscar político, partido, jurisdicción..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mt-10 w-full max-w-md px-4 py-3 rounded-full bg-white/10 border border-red-800 text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
        />

        <p className="mt-4 text-xs text-gray-600">
          RECHAZAR EL LOBBY EXTRANJERO • EXIGIR TRANSPARENCIA • SEGUIR EL DINERO
        </p>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <Link key={p.id} href={`/p/${p.id}`}>
              <div
                className={`rounded-xl overflow-hidden border-2 transition hover:scale-[1.02] cursor-pointer ${
                  p.status === "red"
                    ? "border-red-800 bg-gradient-to-b from-red-950 to-black"
                    : "border-green-800 bg-gradient-to-b from-green-950 to-black"
                }`}
              >
                {p.image_url && (
                  <div className="aspect-[4/5] w-full overflow-hidden border-b border-white/10 bg-black">
                    <img
                      src={p.image_url}
                      alt={`Card verificable de ${p.name}`}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-6">
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">
                    {p.jurisdiction}
                  </p>
                  <h2 className="text-xl font-black text-white leading-tight">
                    {p.name.toUpperCase()}
                  </h2>
                  <p className="text-sm text-gray-400 mt-1">{p.position}</p>
                  <p className="text-xs text-red-400 mt-1">{p.party}</p>

                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-2xl font-black text-white">
                      {formatArs(p.total_lobby_ars)}
                    </p>
                    <p className="text-sm text-red-400">{formatUsd(p.total_lobby_usd)}</p>
                  </div>

                  <div className="mt-3">
                    <span
                      className={`text-xs font-bold px-3 py-1 rounded-full ${
                        p.status === "red"
                          ? "bg-red-900 text-red-300"
                          : "bg-green-900 text-green-300"
                      }`}
                    >
                      {p.status === "red" ? "ISRAEL LOBBY TOTAL" : "ANTI-LOBBY"}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-gray-600 py-20">No se encontraron políticos.</p>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-red-950 py-8 text-center text-xs text-gray-600">
        <p>Lobby Tracker Argentina — Datos de fuentes públicas, contratos oficiales y periodismo de investigación.</p>
        <p className="mt-1">Proyecto sin fines de lucro. La información es poder.</p>
      </footer>
    </main>
  );
}
